import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon, MailIcon, CalendarIcon, ClockIcon, UserCheckIcon, GraduationCapIcon, BookOpenIcon, CheckCircleIcon, XCircleIcon, RotateCcwIcon, UserXIcon, AlertCircleIcon, ChevronDownIcon, EditIcon, ArchiveIcon, ArrowRightIcon } from 'lucide-react';
type Role = 'Primary Admin' | 'Secondary Admin' | 'Educator';
type MemberStatus = 'Active' | 'Archived';
type JoinMethod = 'invited' | 'requested';
interface Member {
  id: number;
  name: string;
  email: string;
  role: Role;
  emailVerified: boolean;
  status: MemberStatus;
  lastActive: string;
  lastActiveDate?: string;
  avatar?: string;
  addedBy: string;
  addedOn: string;
  addedOnDate?: string;
  approvedBy: string;
  approvedOn: string;
  joinMethod: JoinMethod;
  archivedBy?: string;
  archivedOn?: string;
  archivedReason?: string;
  grades?: string[];
  subjects?: string[];
}
interface MemberProfilePanelProps {
  isOpen: boolean;
  member: Member | null;
  onClose: () => void;
  onRestore?: (member: Member) => void;
  onRemoveFromOrg?: (member: Member) => void;
  onArchive?: (member: Member) => void;
  onChangeRole?: (member: Member, newRole: Role) => void;
  onTransferPrimaryAdmin?: (member: Member) => void;
  currentUserRole?: Role;
}
export const MemberProfilePanel: React.FC<MemberProfilePanelProps> = ({
  isOpen,
  member,
  onClose,
  onRestore,
  onRemoveFromOrg,
  onArchive,
  onChangeRole,
  onTransferPrimaryAdmin,
  currentUserRole = 'Primary Admin'
}) => {
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  if (!member) return null;
  const getRoleColor = (role: Role) => {
    switch (role) {
      case 'Primary Admin':
        return 'bg-[#2563EB] bg-opacity-10 text-[#2563EB] border border-[#2563EB] border-opacity-20';
      case 'Secondary Admin':
        return 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] border border-[#00B75F] border-opacity-20';
      case 'Educator':
        return 'bg-surface border border-custom text-text-primary';
    }
  };
  const canRestoreOrRemove = member.status === 'Archived' && currentUserRole === 'Primary Admin';
  const canArchive = member.status === 'Active' && member.role !== 'Primary Admin' && (currentUserRole === 'Primary Admin' || currentUserRole === 'Secondary Admin');
  const canChangeRole = currentUserRole === 'Primary Admin' && member.role !== 'Primary Admin' && member.status === 'Active';
  const canTransferPrimaryAdmin = currentUserRole === 'Primary Admin' && member.role === 'Primary Admin' && member.status === 'Active' && onTransferPrimaryAdmin;
  const handleRoleChange = () => {
    if (selectedRole && onChangeRole && selectedRole !== member.role) {
      onChangeRole(member, selectedRole);
      setIsEditingRole(false);
      setSelectedRole(null);
    }
  };
  return <AnimatePresence data-id="element-1672">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-40" onClick={onClose} data-id="element-1673" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-surface-elevated border-l border-custom shadow-2xl z-50 overflow-y-auto" data-id="element-1674">
            <div className="p-6 space-y-5" data-id="element-1675">
              {/* Header */}
              <div className="flex items-start justify-between" data-id="element-1676">
                <div className="flex items-center gap-4" data-id="element-1677">
                  <div className="w-16 h-16 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-xl flex-shrink-0" data-id="element-1678">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="min-w-0" data-id="element-1679">
                    <h2 className="text-xl font-bold text-text-primary" data-id="element-1680">
                      {member.name}
                    </h2>
                    <p className="text-sm text-text-secondary truncate" data-id="element-1681">
                      {member.email}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0" data-id="element-1682">
                  <XIcon size={20} className="text-text-secondary" data-id="element-1683" />
                </button>
              </div>

              {/* Role & Status - Compact */}
              <div className="bg-surface rounded-xl p-4 border border-custom" data-id="element-1684">
                <div className="flex items-center justify-between mb-3" data-id="element-1685">
                  <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-1686">
                    Role & Status
                  </h3>
                  {canChangeRole && !isEditingRole && <button onClick={() => {
                setIsEditingRole(true);
                setSelectedRole(member.role);
              }} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-[#2563EB] hover:bg-[#2563EB] hover:bg-opacity-10 rounded-lg transition-colors" data-id="element-1687">
                      <EditIcon size={12} data-id="element-1688" />
                      Change
                    </button>}
                  {canTransferPrimaryAdmin && <button onClick={() => onTransferPrimaryAdmin(member)} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-[#2563EB] hover:bg-[#2563EB] hover:bg-opacity-10 rounded-lg transition-colors" data-id="element-1689">
                      <ArrowRightIcon size={12} data-id="element-1690" />
                      Transfer Role
                    </button>}
                </div>

                {isEditingRole ? <div className="space-y-3" data-id="element-1691">
                    {/* Role Options */}
                    <div className="space-y-2" data-id="element-1692">
                      <label className="flex items-center gap-3 p-2.5 rounded-lg border-2 border-custom hover:border-[#00B75F] transition-colors cursor-pointer group" data-id="element-1693">
                        <input type="radio" name="role" value="Educator" checked={selectedRole === 'Educator'} onChange={e => setSelectedRole(e.target.value as Role)} className="w-4 h-4 text-[#00B75F] focus:ring-2 focus:ring-[#00B75F] cursor-pointer" data-id="element-1694" />
                        <div className="flex-1" data-id="element-1695">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1696">
                            Educator
                          </span>
                          <p className="text-xs text-text-tertiary" data-id="element-1697">
                            Can create and manage assessments
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-2.5 rounded-lg border-2 border-custom hover:border-[#00B75F] transition-colors cursor-pointer group" data-id="element-1698">
                        <input type="radio" name="role" value="Secondary Admin" checked={selectedRole === 'Secondary Admin'} onChange={e => setSelectedRole(e.target.value as Role)} className="w-4 h-4 text-[#00B75F] focus:ring-2 focus:ring-[#00B75F] cursor-pointer" data-id="element-1699" />
                        <div className="flex-1" data-id="element-1700">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1701">
                            Secondary Admin
                          </span>
                          <p className="text-xs text-text-tertiary" data-id="element-1702">
                            Can manage members and organization
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-1" data-id="element-1703">
                      <button onClick={handleRoleChange} disabled={selectedRole === member.role} className="flex-1 px-3 py-2 bg-[#00B75F] text-white rounded-lg text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1704">
                        Save
                      </button>
                      <button onClick={() => {
                  setIsEditingRole(false);
                  setSelectedRole(null);
                }} className="px-3 py-2 border-2 border-custom text-text-primary rounded-lg text-sm font-semibold hover:bg-surface transition-colors" data-id="element-1705">
                        Cancel
                      </button>
                    </div>
                  </div> : <div className="grid grid-cols-3 gap-3" data-id="element-1706">
                    <div data-id="element-1707">
                      <p className="text-xs text-text-tertiary mb-1.5" data-id="element-1708">Role</p>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${getRoleColor(member.role)}`} data-id="element-1709">
                        {member.role}
                      </span>
                    </div>
                    <div data-id="element-1710">
                      <p className="text-xs text-text-tertiary mb-1.5" data-id="element-1711">
                        Status
                      </p>
                      {member.status === 'Active' ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00B75F]" data-id="element-1712">
                          <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-1713" />
                          Active
                        </span> : <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF9D33]" data-id="element-1714">
                          <div className="w-2 h-2 rounded-full bg-[#FF9D33]" data-id="element-1715" />
                          Archived
                        </span>}
                    </div>
                    <div data-id="element-1716">
                      <p className="text-xs text-text-tertiary mb-1.5" data-id="element-1717">Email</p>
                      {member.emailVerified ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00B75F]" data-id="element-1718">
                          <CheckCircleIcon size={12} data-id="element-1719" />
                          Verified
                        </span> : <span className="inline-flex items-center gap-1 text-xs font-semibold text-text-tertiary" data-id="element-1720">
                          <XCircleIcon size={12} data-id="element-1721" />
                          Unverified
                        </span>}
                    </div>
                  </div>}
              </div>

              {/* Teaching Context (Educators only) - Moved up */}
              {member.role === 'Educator' && (member.grades || member.subjects) && <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-1722">
                    <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-1723">
                      Teaching Context
                    </h3>
                    {member.grades && member.grades.length > 0 && <div data-id="element-1724">
                        <p className="text-xs text-text-tertiary mb-2" data-id="element-1725">
                          Grades
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-1726">
                          {member.grades.map((grade, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-surface-elevated rounded-lg text-xs font-medium text-text-primary border border-custom" data-id="element-1727">
                              <GraduationCapIcon size={12} data-id="element-1728" />
                              {grade}
                            </span>)}
                        </div>
                      </div>}
                    {member.subjects && member.subjects.length > 0 && <div data-id="element-1729">
                        <p className="text-xs text-text-tertiary mb-2" data-id="element-1730">
                          Subjects
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-1731">
                          {member.subjects.map((subject, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-surface-elevated rounded-lg text-xs font-medium text-text-primary border border-custom" data-id="element-1732">
                              <BookOpenIcon size={12} data-id="element-1733" />
                              {subject}
                            </span>)}
                        </div>
                      </div>}
                  </div>}

              {/* Membership Details - Condensed */}
              <div className="bg-surface rounded-xl p-4 border border-custom" data-id="element-1734">
                <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wide mb-3" data-id="element-1735">
                  Membership
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-id="element-1736">
                  <div data-id="element-1737">
                    <p className="text-text-tertiary" data-id="element-1738">Joined</p>
                    <p className="text-text-primary font-medium" data-id="element-1739">
                      {member.addedOn}
                    </p>
                  </div>
                  <div data-id="element-1740">
                    <p className="text-text-tertiary" data-id="element-1741">Last Active</p>
                    <p className="text-text-primary font-medium" data-id="element-1742">
                      {member.lastActive}
                    </p>
                  </div>
                  <div data-id="element-1743">
                    <p className="text-text-tertiary" data-id="element-1744">Added By</p>
                    <p className="text-text-primary font-medium" data-id="element-1745">
                      {member.addedBy}
                    </p>
                  </div>
                  <div data-id="element-1746">
                    <p className="text-text-tertiary" data-id="element-1747">Method</p>
                    <p className="text-text-primary font-medium" data-id="element-1748">
                      {member.joinMethod === 'invited' ? 'Invited' : 'Requested'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Archived Info */}
              {member.status === 'Archived' && <div className="bg-[#FF9D33] bg-opacity-5 border border-[#FF9D33] border-opacity-30 rounded-xl p-4" data-id="element-1749">
                  <div className="flex items-start gap-2 mb-3" data-id="element-1750">
                    <AlertCircleIcon size={14} className="text-[#FF9D33] flex-shrink-0 mt-0.5" data-id="element-1751" />
                    <h3 className="text-xs font-bold text-[#FF9D33] uppercase tracking-wide" data-id="element-1752">
                      Archived
                    </h3>
                  </div>
                  <div className="space-y-2 text-xs" data-id="element-1753">
                    {member.archivedBy && <div className="flex justify-between" data-id="element-1754">
                        <span className="text-text-tertiary" data-id="element-1755">By</span>
                        <span className="text-text-primary font-medium" data-id="element-1756">
                          {member.archivedBy}
                        </span>
                      </div>}
                    {member.archivedOn && <div className="flex justify-between" data-id="element-1757">
                        <span className="text-text-tertiary" data-id="element-1758">On</span>
                        <span className="text-text-primary font-medium" data-id="element-1759">
                          {member.archivedOn}
                        </span>
                      </div>}
                    {member.archivedReason && <div data-id="element-1760">
                        <p className="text-text-tertiary mb-1" data-id="element-1761">Reason</p>
                        <p className="text-text-primary" data-id="element-1762">
                          {member.archivedReason}
                        </p>
                      </div>}
                  </div>
                </div>}

              {/* Archive Action (for Active members) */}
              {canArchive && onArchive && <div className="pt-4 border-t border-custom" data-id="element-1763">
                  <button onClick={() => onArchive(member)} className="w-full px-4 py-2.5 bg-[#FF9D33] bg-opacity-10 text-[#FF9D33] rounded-lg font-semibold hover:bg-[#FF9D33] hover:bg-opacity-20 transition-colors flex items-center justify-center gap-2 border border-[#FF9D33] border-opacity-30 text-sm" data-id="element-1764">
                    <ArchiveIcon size={16} data-id="element-1765" />
                    Archive Member
                  </button>
                  <p className="text-xs text-text-tertiary text-center mt-2" data-id="element-1766">
                    Change status to Archived (remains in organization)
                  </p>
                </div>}

              {/* Restore and Remove from Org Actions (for Archived members) */}
              {canRestoreOrRemove && onRestore && onRemoveFromOrg && <div className="pt-4 border-t border-custom space-y-2" data-id="element-1767">
                  <button onClick={() => onRestore(member)} className="w-full px-4 py-2.5 bg-[#00B75F] text-white rounded-lg font-semibold hover:bg-[#00a055] transition-colors flex items-center justify-center gap-2 shadow-sm text-sm" data-id="element-1768">
                    <RotateCcwIcon size={16} data-id="element-1769" />
                    Restore to Active
                  </button>

                  <button onClick={() => onRemoveFromOrg(member)} className="w-full px-4 py-2.5 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2 border border-red-200 text-sm" data-id="element-1770">
                    <UserXIcon size={16} data-id="element-1771" />
                    Remove from Org
                  </button>

                  <p className="text-xs text-text-tertiary text-center pt-1" data-id="element-1772">
                    Restore status or delink from organization
                  </p>
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};